import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FiActivity,
  FiCheckCircle,
  FiClock,
  FiRefreshCw,
  FiX,
  FiXCircle,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";
import { config } from "../../../../../../utils/apiUrl";
import useSwal from "../../../../../../hooks/useSwal";

import {
  REQUIREMENT_STATUSES,
  getCustomerName,
} from "./constants";

const STATUS_META = {
  Active: {
    icon: FiActivity,
    description:
      "The requirement is open and waiting for action.",
  },

  "In Progress": {
    icon: FiClock,
    description:
      "Work has started on this requirement.",
  },

  Completed: {
    icon: FiCheckCircle,
    description:
      "The requirement has been completed successfully.",
  },

  Closed: {
    icon: FiXCircle,
    description:
      "The requirement is closed and no further action is required.",
  },
};

const ChangeStatusDialog = ({
  open,
  requirement,
  onClose,
  onSuccess,
}) => {
  const api = useMemo(() => new API(), []);
  const swal = useSwal();

  const [status, setStatus] =
    useState("");

  const [submitting, setSubmitting] =
    useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setStatus(
      requirement?.status || "Active"
    );

    setSubmitting(false);
  }, [open, requirement]);

  if (!open || !requirement) {
    return null;
  }

  const customerName =
    getCustomerName(
      requirement?.customer
    );

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();

    if (!status) {
      return;
    }

    try {
      setSubmitting(true);

      const response =
        await api.patch(
          config.changeRequirementStatus(
            requirement._id
          ),
          {
            status,
          }
        );

      if (
        response?.status ===
        "success"
      ) {
        await swal.success({
          icon: "success",
          title: "Status Updated",
          text:
            response?.message ||
            "Requirement status updated successfully.",
          timer: 1400,
          showConfirmButton: false,
        });

        if (
          typeof onSuccess ===
          "function"
        ) {
          onSuccess(
            response?.data || null
          );
        }
      } else {
        throw new Error(
          response?.message ||
            "Unable to update status."
        );
      }
    } catch (requestError) {
      console.error(
        "Change requirement status error:",
        requestError
      );

      swal.error({
        icon: "error",
        title:
          "Unable to update status",
        text:
          requestError?.response?.data
            ?.message ||
          requestError?.message ||
          "Unable to update requirement status.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="requirement-status-overlay">
      <div
        className="requirement-status-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Change requirement status"
      >
        <div className="requirement-status-header">
          <div>
            <span>
              MANAGE REQUIREMENT
            </span>

            <h3>
              Change Status
            </h3>
          </div>

          <button
            type="button"
            className="requirement-status-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="requirement-status-body">
            <div className="requirement-status-info">
              <FiRefreshCw />

              <div>
                <span>
                  Requirement
                </span>

                <strong>
                  {requirement?.requirementType ||
                    "-"}
                </strong>

                <small>
                  Customer: {customerName}
                </small>
              </div>
            </div>

            <div className="requirement-status-options">
              {REQUIREMENT_STATUSES.map(
                (statusOption) => {
                  const meta =
                    STATUS_META[
                      statusOption
                    ];

                  const Icon =
                    meta?.icon ||
                    FiActivity;

                  const selected =
                    status ===
                    statusOption;

                  return (
                    <button
                      key={statusOption}
                      type="button"
                      className={`requirement-status-option ${
                        selected
                          ? "selected"
                          : ""
                      }`}
                      onClick={() =>
                        setStatus(
                          statusOption
                        )
                      }
                      disabled={
                        submitting
                      }
                    >
                      <div className="requirement-status-option-icon">
                        <Icon />
                      </div>

                      <div className="requirement-status-option-content">
                        <strong>
                          {statusOption}
                        </strong>

                        <span>
                          {
                            meta?.description
                          }
                        </span>
                      </div>

                      <div className="requirement-status-radio">
                        <span />
                      </div>
                    </button>
                  );
                }
              )}
            </div>
          </div>

          <div className="requirement-status-footer">
            <button
              type="button"
              className="requirement-status-cancel"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="requirement-status-save"
              disabled={
                submitting ||
                status ===
                  requirement?.status
              }
            >
              <FiRefreshCw />

              {submitting
                ? "Updating..."
                : "Update Status"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangeStatusDialog;